import { useState, useMemo } from 'react';
import { useUser, SignInButton } from '@clerk/nextjs';
import { useToast } from '@/hooks/use-toast';
import { useQuery } from 'convex/react';
import { api } from '@/convex/_generated/api';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  Check,
  ShoppingCart,
  Database,
  ChevronUp,
  ChevronDown,
  LogIn,
  Loader2,
} from 'lucide-react';
// import { createStripeCheckoutSession } from '@/app/actions/createStripeCheckoutSession';
import { Id } from '@/convex/_generated/dataModel';
import JsonTable from './JsonTable';
import { useAnalytics } from '@/lib/analytics/useAnalytics';

interface PurchaseButtonProps {
  eventId: Id<'events'>;
  price: number;
  sampleData?: string;
  variant?: 'default' | 'large';
  className?: string;
}

export default function PurchaseButton({
  eventId,
  price,
  variant = 'default',
  className,
}: PurchaseButtonProps) {
  const [isProcessing, setIsProcessing] = useState(false);
  const { user, isSignedIn } = useUser();
  const { toast } = useToast();
  const { trackEvent } = useAnalytics();

  // Prevent purchase if user is the seller
  const isSellerQuery = useQuery(api.users.isEventSeller, {
    eventId: eventId,
    userId: user?.id || '',
  });

  if (isSellerQuery) {
    trackEvent('list_view_own', { eventId: eventId.toString() });
    return (
      <div className="w-full px-4 py-2.5 bg-gray-50 text-gray-600 rounded-lg text-center border border-gray-200">
        <div className="flex items-center justify-center gap-1">
          <Check className="w-4 h-4 text-gray-500" />
          <span className="font-medium text-sm">You're the seller</span>
        </div>
      </div>
    );
  }

  const handlePurchase = async () => {
    if (!isSignedIn) {
      trackEvent('purchase_attempt_not_signed_in', { eventId: eventId.toString(), price });
      // We don't need to do anything here as the SignInButton will handle the sign-in flow
      return;
    }

    trackEvent('purchase_initiated', {
      eventId: eventId.toString(),
      price,
      userId: user.id,
    });

    setIsProcessing(true);
    try {
      // Get the primary email address
      const userEmail = user.primaryEmailAddress?.emailAddress || '';

      // Use the checkout-sessions API endpoint instead of the server action
      const response = await fetch('/api/checkout-sessions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          productId: eventId,
          productType: 'list',
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create checkout session');
      }

      const { sessionUrl, sessionId, paymentId } = await response.json();

      if (!sessionUrl) throw new Error('No session URL returned');

      trackEvent('checkout_session_created', {
        eventId: eventId.toString(),
        price,
        userId: user.id,
        sessionId,
        paymentId,
      });

      window.location.href = sessionUrl;
    } catch (error) {
      console.error('Purchase error:', error);

      trackEvent('purchase_error', {
        eventId: eventId.toString(),
        error: error instanceof Error ? error.message : 'Unknown error',
        userId: user?.id,
      });

      toast({
        title: 'Error',
        description: 'Failed to process purchase. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsProcessing(false);
    }
  };

  // Return different button based on sign-in status
  if (!isSignedIn) {
    return (
      <SignInButton mode="modal">
        <button
          onClick={() =>
            trackEvent('purchase_sign_in_click', { eventId: eventId.toString(), price })
          }
          className={cn(
            'w-full flex items-center justify-center gap-2 font-medium transition-colors',
            variant === 'large' ? 'py-3 px-6 text-base' : 'py-2 px-4 text-sm',
            'bg-blue-600 hover:bg-blue-700 text-white rounded-lg',
            className,
          )}
        >
          Sign in to Purchase
        </button>
      </SignInButton>
    );
  }

  return (
    <button
      onClick={handlePurchase}
      disabled={isProcessing}
      className={cn(
        'w-full flex items-center justify-center gap-2 font-medium transition-colors',
        variant === 'large' ? 'py-3 px-6 text-base' : 'py-2 px-4 text-sm',
        'bg-blue-600 hover:bg-blue-700 text-white rounded-lg',
        isProcessing ? 'opacity-70 cursor-not-allowed' : '',
        className,
      )}
    >
      {isProcessing ? (
        <>
          <Loader2 className="w-4 h-4 animate-spin" />
          Processing...
        </>
      ) : (
        <>Buy for £{price}</>
      )}
    </button>
  );
}
